import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import useModalStore from '@/store/useModalStore';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Calendar,
  CheckCircle2,
  Clock,
  Coins,
  CreditCard,
  Lock,
  TrendingUp,
  Users,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const FORECAST_HOW_IT_WORKS_MODAL_KEY = 'forecast-how-it-works';

export default function ForecastHowItWorksModal() {
  const { t } = useTranslation('wallet');
  const { modals, closeModal } = useModalStore();

  const isOpen = !!modals[FORECAST_HOW_IT_WORKS_MODAL_KEY];

  const handleClose = () => closeModal(FORECAST_HOW_IT_WORKS_MODAL_KEY);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[95vw] sm:w-[90vw] max-w-2xl max-h-[85vh] overflow-hidden p-0 rounded-lg">
        <AnimatePresence mode="wait">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="flex flex-col max-h-[85vh] min-w-0"
          >
            <DialogHeader className="p-3 sm:p-6 pb-3 sm:pb-4 border-b border-colorBorder shrink-0 text-left">
              <div className="flex items-center gap-2 sm:gap-3 pr-6">
                <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-primary/10 border border-primary/20 shrink-0">
                  <TrendingUp size={16} className="text-primary sm:w-5 sm:h-5" />
                </div>
                <div className="min-w-0">
                  <DialogTitle className="text-sm sm:text-xl font-semibold">
                    {t('forecastHowItWorks.title')}
                  </DialogTitle>
                  <p className="text-muted-foreground text-[10px] sm:text-sm mt-0.5">
                    {t('forecastHowItWorks.subtitle')}
                  </p>
                </div>
              </div>
            </DialogHeader>

            <div className="overflow-y-auto flex-1 p-3 sm:p-6 space-y-3 sm:space-y-5 min-w-0">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="bg-zinc-900/50 rounded-lg p-3 sm:p-4 border border-colorBorder"
              >
                <p className="text-colorText text-xs sm:text-sm font-medium mb-3">
                  {t('forecastHowItWorks.introText')}
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-5 h-5 rounded-full bg-[#A9D342]/20 shrink-0">
                      <CheckCircle2 size={12} className="text-[#A9D342]" />
                    </div>
                    <span className="text-muted-foreground text-xs sm:text-sm">
                      {t('forecastHowItWorks.introFeature1')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-5 h-5 rounded-full bg-[#A9D342]/20 shrink-0">
                      <CheckCircle2 size={12} className="text-[#A9D342]" />
                    </div>
                    <span className="text-muted-foreground text-xs sm:text-sm">
                      {t('forecastHowItWorks.introFeature2')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-5 h-5 rounded-full bg-[#A9D342]/20 shrink-0">
                      <CheckCircle2 size={12} className="text-[#A9D342]" />
                    </div>
                    <span className="text-muted-foreground text-xs sm:text-sm">
                      {t('forecastHowItWorks.introFeature3')}
                    </span>
                  </div>
                </div>
                <p className="text-muted-foreground text-[10px] sm:text-xs mt-3 leading-relaxed">
                  {t('forecastHowItWorks.introFooter')}
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.15 }}
                className="bg-zinc-900/50 rounded-lg p-3 sm:p-4 border border-colorBorder"
              >
                <div className="flex items-start gap-2.5 mb-2">
                  <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-sky-500/10 border border-sky-500/20 shrink-0 mt-0.5">
                    <CreditCard size={14} className="text-sky-400" />
                  </div>
                  <div>
                    <h3 className="text-xs sm:text-sm font-semibold text-colorText">
                      {t('forecastHowItWorks.section1Title')}
                    </h3>
                    <p className="text-muted-foreground text-[10px] sm:text-xs leading-relaxed mt-1">
                      {t('forecastHowItWorks.section1Text')}
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="bg-zinc-900/50 rounded-lg p-3 sm:p-4 border border-colorBorder"
              >
                <div className="flex items-start gap-2.5 mb-2">
                  <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-amber-500/10 border border-amber-500/20 shrink-0 mt-0.5">
                    <Lock size={14} className="text-amber-400" />
                  </div>
                  <div>
                    <h3 className="text-xs sm:text-sm font-semibold text-colorText">
                      {t('forecastHowItWorks.section2Title')}
                    </h3>
                    <p className="text-muted-foreground text-[10px] sm:text-xs leading-relaxed mt-1">
                      {t('forecastHowItWorks.section2Text')}
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.25 }}
                className="bg-zinc-900/50 rounded-lg p-3 sm:p-4 border border-colorBorder"
              >
                <div className="flex items-start gap-2.5 mb-2">
                  <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-emerald-500/10 border border-emerald-500/20 shrink-0 mt-0.5">
                    <Calendar size={14} className="text-emerald-500" />
                  </div>
                  <div>
                    <h3 className="text-xs sm:text-sm font-semibold text-colorText">
                      {t('forecastHowItWorks.section3Title')}
                    </h3>
                    <p className="text-muted-foreground text-[10px] sm:text-xs leading-relaxed mt-1">
                      {t('forecastHowItWorks.section3Text')}
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="bg-zinc-900/50 rounded-lg p-3 sm:p-4 border border-colorBorder"
              >
                <div className="flex items-start gap-2.5 mb-2">
                  <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-violet-500/10 border border-violet-500/20 shrink-0 mt-0.5">
                    <Users size={14} className="text-violet-400" />
                  </div>
                  <div>
                    <h3 className="text-xs sm:text-sm font-semibold text-colorText">
                      {t('forecastHowItWorks.section4Title')}
                    </h3>
                    <p className="text-muted-foreground text-[10px] sm:text-xs leading-relaxed mt-1">
                      {t('forecastHowItWorks.section4Text')}
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="p-3 sm:p-6 pt-3 sm:pt-4 border-t border-colorBorder bg-background shrink-0"
            >
              <Button onClick={handleClose} className="w-full h-9 sm:h-10 text-xs sm:text-sm">
                <CheckCircle2 className="mr-2 h-4 w-4" />
                {t('forecastHowItWorks.closeButton')}
              </Button>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
